import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLanguage } from "@/App";
import { translations } from "@/i18n";

const HERO_IMG =
  "https://images.pexels.com/photos/2036432/pexels-photo-2036432.jpeg?auto=compress&cs=tinysrgb&w=2000";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = (id) => {
    if (lenis) lenis.scrollTo(id, { offset: -72 });
    else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div className="absolute inset-0 scale-110" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Las Vegas skyline at dusk"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A1128]/70" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#0A1128] to-transparent" />
      </motion.div>

      <span
        aria-hidden
        className="pointer-events-none absolute -right-8 top-1/2 hidden -translate-y-1/2 select-none font-kr text-[22rem] font-black leading-none text-white/[0.04] lg:block"
      >
        코암
      </span>

      <motion.div
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-32 pb-24 lg:px-10"
        style={{ opacity: contentOpacity }}
      >
        <MaskedLine delay={0.15}>
          <span className="mb-8 inline-flex items-center gap-3 border border-[#D4AF37]/40 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.3em] text-[#D4AF37]">
            <span className="relative flex h-3 w-3 overflow-hidden rounded-full">
              <span className="h-3 w-1.5 bg-[#C8102E]" />
              <span className="h-3 w-1.5 bg-[#3E7BD9]" />
            </span>
            {t.hero.eyebrow}
          </span>
        </MaskedLine>

        <h1
          data-testid="hero-headline"
          className="font-display text-4xl font-black leading-[1.05] tracking-tighter text-white sm:text-5xl lg:text-7xl"
        >
          {t.hero.lines.map((line, i) => (
            <MaskedLine key={`${lang}-${i}`} delay={0.3 + i * 0.15}>
              {i === 1 ? (
                <span className="text-[#D4AF37]">{line}</span>
              ) : (
                line
              )}
            </MaskedLine>
          ))}
        </h1>

        <div className="mt-10 max-w-xl">
          <MaskedLine delay={0.85}>
            <p className="text-base font-light leading-relaxed text-slate-300 md:text-lg">
              {t.hero.sub}
            </p>
          </MaskedLine>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-col gap-4 sm:flex-row"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => go("#contact")}
            className="group flex items-center justify-center gap-3 bg-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-widest text-[#0A1128] transition-colors duration-300 hover:bg-white"
          >
            {t.hero.primaryCta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => go("#services")}
            className="flex items-center justify-center gap-3 border border-white/25 px-8 py-4 text-sm font-medium uppercase tracking-widest text-white transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            {t.hero.secondaryCta}
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 text-[11px] uppercase tracking-[0.35em] text-white/40"
          data-testid="hero-badge"
        >
          {t.hero.badge}
        </motion.p>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => go("#why-nevada")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3 text-white/40 transition-colors duration-300 hover:text-[#D4AF37]"
        aria-label={t.hero.scroll}
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
